import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import { ErrorResponse } from '@apollo/client/link/error';
import * as Sentry from '@sentry/react';
import { useBackdropContext } from '../../context/backdrop';
import { useUpdateChannel } from '../../integrations/lavva/api/channels';
import { LavvaResolve, PublishStatus } from '../../types';
import { isLocalApp } from '../../utils/helpers';
import { toastError } from '../../utils/toast';

const errorsPartForReports = ['HC0018', 'EXEC', 'LEAF', 'AUTH', 'UNKNOWN'];
const errorsWithShouldReload = ['AUTH_NOT_AUTHORIZED', 'AUTH_NOT_AUTHENTICATED'];
const networkErrorsForReport = ['AUTH_NOT_AUTHENTICATED', 'ServerError'];
const schemaRelatedErrors = ['GRAPHQL_PARSE_FAILED', 'GRAPHQL_VALIDATION_FAILED', 'BAD_USER_INPUT', 'INVALID_ARGUMENT'];

const reportUnhandledErrors = (message: string) => {
  if (errorsPartForReports.some((errorPart) => message.includes(errorPart)) && !isLocalApp) {
    Sentry.captureMessage(message, { level: 'info' });
  }
};

const isSchemaRelatedError = (error: any): boolean => {
  const errorCode = error.extensions?.code as string;
  const errorMessage = error.message || '';

  if (errorCode && schemaRelatedErrors.includes(errorCode)) return true;

  const schemaKeywords = ['schema', 'validation', 'parse', 'syntax', 'field', 'argument'];
  return schemaKeywords.some((keyword) => errorMessage.toLowerCase().includes(keyword));
};

export const useErrors = () => {
  const { t } = useTranslation('common');
  const { t: tv } = useTranslation('backend-validation');
  const { turnOffBackdrop } = useBackdropContext();
  const { setChannelAsDisconnected } = useUpdateChannel();

  const handleGraphQLError = ({ graphQLErrors, networkError, operation }: ErrorResponse) => {
    if (operation.operationName === 'AddMultipleInstallationsAfterMigration') return;

    if (graphQLErrors) {
      graphQLErrors.forEach((error) => {
        const code: string = error.extensions?.code as string;
        console.log('GQL Error', JSON.stringify({ error, networkError, operation }));

        // Skip showing toast errors for schema-related errors if it's a query
        const isQuery = operation.query?.definitions?.some(
          (def) => def.kind === 'OperationDefinition' && def.operation === 'query',
        );

        if (isSchemaRelatedError(error) && isQuery) {
          console.log('Schema-related error in query detected, skipping toast notification:', error.message);
          return;
        }

        switch (code) {
          case 'installation_not_attached': {
            console.log('INSTALLATION NOT ATTACHED');
            break;
          }
          default: {
            const errorCode: string = error.extensions?.code as string;

            if (errorCode) {
              if (errorCode.includes('MAXIMUM_LENGTH')) {
                showError(`${tv('MAXIMUM_LENGTH', { value: errorCode.split('_')[2] })}`);
              } else if (errorCode.includes('NOT_GREATER_THAN')) {
                showError(`${tv('NOT_GREATER_THAN', { value: errorCode.split('_')[3] })}`);
              } else {
                showError(tv(errorCode) || error.message);
              }
            } else {
              showError(error.message);
            }

            break;
          }
        }
      });
    }

    if (networkError) {
      console.log('NETWORK ERROR', {
        networkError: JSON.stringify(networkError),
        operation: JSON.stringify(operation),
      });

      if (!isLocalApp && networkErrorsForReport.includes(networkError.name)) {
        Sentry.captureMessage(networkError.name, {
          level: 'info',
          extra: {
            name: networkError.name,
            message: networkError.message,
            cause: networkError.cause,
            stack: networkError.stack,
            operation: operation.operationName,
            graphQLErrors,
          },
        });
      }
    }
  };

  const handleRestErrors = (error: AxiosError, errorMessage?: string) => {
    turnOffBackdrop();
    if (error.code === 'ERR_CANCELED') return;

    const status = error.response?.status;

    if (status) {
      switch (status) {
        case 401: {
          window.location.reload();
          break;
        }
        case 429: {
          showError(t('errors.tooManyRequests'));
          break;
        }
        case 500: {
          showError(t('errors.somethingWentWrong'));
          break;
        }
        case 501: {
          showError(t('errors.somethingWentWrong'));
          break;
        }
        case 502: {
          showError(t('errors.somethingWentWrong'));
          break;
        }
        case 503: {
          showError(t('errors.somethingWentWrong'));
          break;
        }
        default: {
          if (errorMessage) toastError({ content: errorMessage });
          else if (typeof error.response?.data === 'string') {
            showError(error.response?.data || t('errors.somethingWentWrong'));
          } else {
            const errorData = error.response?.data as any;
            if (errorData) {
              console.log(errorData || 'Error');

              if ('ErrorCode' in errorData) {
                showError(tv(errorData.ErrorCode));
              } else if ('errorCode' in errorData) showError(tv(errorData.errorCode));
            }
          }
          break;
        }
      }
    } else showError(t('errors.somethingWentWrong'));
  };

  const showError = (message: string) => {
    if (errorsWithShouldReload.includes(message)) {
      window.location.reload();
    } else {
      toastError({ content: message });
      reportUnhandledErrors(message);
    }
  };

  const handleLavvaResolve = (params: LavvaResolve) => {
    turnOffBackdrop();

    switch (params.status) {
      case PublishStatus.Ok: {
        if (params.onSuccess) params.onSuccess();
        break;
      }
      case PublishStatus.DeviceOrChannelNotFound: {
        toastError({ content: t(`publishStatus.${PublishStatus[PublishStatus.DeviceOrChannelNotFound]}`) });
        break;
      }
      case PublishStatus.DeviceDisconnected: {
        toastError({ content: t(`publishStatus.${PublishStatus[PublishStatus.DeviceDisconnected]}`) });
        if (params.deviceId) setChannelAsDisconnected(params.deviceId);
        break;
      }
      case PublishStatus.ContractNotSupported: {
        toastError({ content: t(`publishStatus.${PublishStatus[PublishStatus.ContractNotSupported]}`) });
        break;
      }
      default: {
        break;
      }
    }
  };

  return { handleGraphQLError, handleRestErrors, handleLavvaResolve };
};
